import {
  cargoRemaining,
  charterStatus,
  cospReference,
  cpComparison,
  type DailyKind,
  phaseReference,
  previousFlowReport,
  speedDiffPct,
  voyageDistanceRun,
} from '@felix/domain';
import type { Content } from 'pdfmake/interfaces.js';
import { formatHours } from '../../format.js';
import { ltUtcText } from '../../inputs/lt-utc.js';
import { formatLatitude, formatLongitude, noonDerived, previousLeg } from './noon-report.js';
import type { ReportT } from './report-pdf.js';
import type { Voyage, VoyageReport } from './types.js';

/**
 * Noon panelleri ve günlük raporların PDF bölümleri (ADR 0059 §8): Noon'da COSP referansı, önceki
 * rapor, ETA ve liman, iki slip ve akıntı etkisi, CP durumu ve günlük performansı, çalışma
 * saatleri; günlükte konum durumu, aşama referansı, son rapordan beri, ETD, yük ilerlemesi,
 * çalışma saatleri. Türetilen değerler ekrandaki domain hesaplarıyla aynıdır. Boş bölüm basılmaz.
 */

export interface PdfBuilders {
  heading: (value: string) => Content;
  facts: (pairs: [string, string][]) => Content;
  table: (head: string[], body: string[][], widths: (string | number)[]) => Content;
  time: (value: unknown) => string;
}

export type PdfVoyage = Pick<Voyage, 'voyageNo' | 'fromPort' | 'toPort'> &
  Partial<
    Pick<
      Voyage,
      'cpSpeedLadenKn' | 'cpSpeedBallastKn' | 'cpConsumptionLadenMt' | 'cpConsumptionBallastMt'
    >
  >;

const num = (value: unknown, digits = 1) =>
  typeof value === 'number' && Number.isFinite(value) ? value.toFixed(digits) : '—';
const record = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
const rows = (value: unknown): Record<string, unknown>[] =>
  Array.isArray(value) ? value.map(record) : [];
const unit = (value: unknown, label: string, digits = 1) =>
  typeof value === 'number' && Number.isFinite(value) ? `${num(value, digits)} ${label}` : '—';
const textOr = (value: unknown) => (typeof value === 'string' && value.trim() ? value : '—');

/** Bilinen değeri olan çiftlerle başlık + tablo; hiçbiri yoksa basılmaz. */
function section(b: PdfBuilders, title: string, pairs: [string, string][]): Content[] {
  const filled = pairs.filter(([, value]) => value !== '—');
  return filled.length > 0 ? [b.heading(title), b.facts(filled)] : [];
}

const othersOf = (report: VoyageReport, reports: readonly VoyageReport[]) =>
  reports.filter((r) => r.id !== report.id && r.status === 'submitted');

export function noonPdfContent(
  t: ReportT,
  report: VoyageReport,
  reports: readonly VoyageReport[],
  voyage: PdfVoyage | null,
  b: PdfBuilders,
): Content[] {
  const r = (key: string, options?: Record<string, unknown>) =>
    t(`voyage.noon.reference.${key}`, options ?? {});
  const m = (key: string) => t(`voyage.noon.machinery.${key}`);
  const out: Content[] = [];
  const others = othersOf(report, reports);
  const nav = record(report.data.navigation);
  const distance = typeof nav.distanceSinceLast === 'number' ? nav.distanceSinceLast : null;
  const current = { reportedAt: report.reportedAt, distanceNm: distance };
  const cosp = cospReference(others, current);
  if (cosp) {
    out.push(
      ...section(b, r('cosp.title'), [
        [r('cosp.time'), ltUtcText(cosp.cosp.reportedAt, cosp.cosp.tzOffsetMin)],
        [
          r('cosp.position'),
          cosp.cosp.lat !== null && cosp.cosp.lon !== null
            ? `${formatLatitude(cosp.cosp.lat)} ${formatLongitude(cosp.cosp.lon)}`
            : '—',
        ],
      ]),
    );
  }
  const previous = previousFlowReport(others, report.reportedAt);
  if (previous) {
    out.push(
      ...section(b, r('previous.title'), [
        [r('previous.type'), t(`voyage.kinds.${previous.kind}`)],
        [r('previous.time'), ltUtcText(previous.reportedAt, previous.tzOffsetMin)],
      ]),
    );
  }
  const remaining = typeof nav.distanceToGo === 'number' ? nav.distanceToGo : null;
  const run = voyageDistanceRun(others, current);
  const before = previousLeg(others, report.reportedAt);
  const derived = noonDerived(report, before?.reportedAt ?? null);
  // Son rapordan beri ve COSP'tan beri ayrı bölümler (04.11 A-5; ekrandaki iki kutu)
  out.push(
    ...section(b, r('sinceLast.title'), [
      [r('sinceLast.distance'), unit(distance, 'NM')],
      [r('sinceLast.elapsed'), previous ? formatHours(previous.hours) : '—'],
      [r('sinceLast.avgSpeed'), unit(derived.averageSpeedKn, 'kn', 2)],
    ]),
    ...section(b, r('sinceCosp.title'), [
      [r('sinceCosp.distance'), cosp ? unit(cosp.distanceNm, 'NM') : '—'],
      [r('sinceCosp.elapsed'), cosp ? unit(cosp.hours, 'h') : '—'],
      [r('sinceCosp.avgSpeed'), cosp ? unit(cosp.avgSpeedKn, 'kn', 2) : '—'],
      [r('sinceCosp.remaining'), unit(remaining, 'NM')],
    ]),
  );
  out.push(
    ...section(b, r('eta.title'), [
      [r('eta.nextPort'), textOr(nav.nextPort)],
      [r('eta.chartArea'), textOr(nav.chartArea)],
      [r('eta.eta'), b.time(nav.eta)],
      [r('eta.total'), remaining === null ? '—' : unit(run + remaining, 'NM')],
    ]),
  );

  // İki slip ve akıntı etkisi (ADR 0059 §7)
  out.push(
    ...section(b, m('slipDetails'), [
      [m('slipShort'), unit(derived.slipPct, '%', 2)],
      [m('slipSog'), unit(derived.slipSogPct, '%', 2)],
      [
        m('currentEffect'),
        derived.currentEffectKn === null
          ? '—'
          : `${unit(derived.currentEffectKn, 'kn', 2)} (${unit(derived.currentEffectPct, '%', 2)})`,
      ],
    ]),
  );

  // CP durumu ve günlük performans (ekrandaki CpCard ile aynı hesap)
  const c = (key: string) => t(`voyage.noon.cp.${key}`);
  const status = charterStatus(others, report.reportedAt);
  const load = [...others]
    .filter((x) => x.kind === 'cosp' && Date.parse(x.reportedAt) <= Date.parse(report.reportedAt))
    .sort((a, z) => Date.parse(a.reportedAt) - Date.parse(z.reportedAt))
    .at(-1)?.data.loadCondition;
  const daily =
    derived.totalConsumptionMt !== null && derived.steamingHours
      ? (derived.totalConsumptionMt / derived.steamingHours) * 24
      : null;
  const cp = voyage
    ? cpComparison(
        {
          avgSpeedKn: derived.averageSpeedKn,
          dailyConsumptionMt: daily,
          loadCondition: load === 'laden' || load === 'ballast' ? load : null,
        },
        voyage,
      )
    : null;
  const pct = cp ? speedDiffPct(cp.plannedSpeedKn, cp.speedDiffKn) : null;
  out.push(
    ...section(b, c('title'), [
      [c('status'), status ? c(`statuses.${status}`) : '—'],
      [c('plannedSpeed'), unit(cp?.plannedSpeedKn, 'kn')],
      [
        c('actualSpeed'),
        cp?.actualSpeedKn == null
          ? '—'
          : `${unit(cp.actualSpeedKn, 'kn')}${cp.speedDiffKn === null ? '' : ` (${cp.speedDiffKn > 0 ? '+' : ''}${num(cp.speedDiffKn)} kn${pct === null ? '' : `, ${pct > 0 ? '+' : ''}${num(pct)} %`})`}`,
      ],
      [c('plannedConsumption'), unit(cp?.plannedConsumptionMt, 'MT/day')],
      [c('actualConsumption'), unit(cp?.actualConsumptionMt, 'MT/day')],
    ]),
  );

  // Çalışma saatleri: ana makine ve yardımcılar (toplam sayaç ve son rapordan beri)
  const machinery = record(report.data.machinery);
  const engine = record(machinery.mainEngine);
  const hours: string[][] = [];
  if (typeof engine.runningHours === 'number' || typeof engine.hoursSinceLast === 'number') {
    hours.push([m('mainEngine'), num(engine.hoursSinceLast), num(engine.runningHours)]);
  }
  for (const row of rows(machinery.auxiliaries)) {
    if (typeof row.runningHours !== 'number' && typeof row.hoursSinceLast !== 'number') continue;
    hours.push([textOr(row.name), num(row.hoursSinceLast), num(row.runningHours)]);
  }
  if (hours.length > 0) out.push(...hoursTable(t, b, hours, false));
  return out;
}

function hoursTable(t: ReportT, b: PdfBuilders, body: string[][], today: boolean): Content[] {
  const h = (key: string) => t(`voyage.daily.hours.${key}`);
  const head = today
    ? [h('equipment'), h('sinceLast'), h('today'), h('total'), h('remarks')]
    : [h('equipment'), h('sinceLast'), h('total')];
  const widths = today ? ['*', 70, 60, 80, 100] : ['*', 90, 110];
  return [b.heading(h('title')), b.table(head, body, widths)];
}

export function dailyPdfContent(
  t: ReportT,
  kind: DailyKind,
  report: VoyageReport,
  reports: readonly VoyageReport[],
  b: PdfBuilders,
): Content[] {
  const d = (key: string) => t(`voyage.daily.${key}`);
  const out: Content[] = [];
  const data = report.data;
  const location = record(data.location);
  out.push(
    ...section(b, d('location.title'), [
      [d(`location.port.${kind}`), textOr(location.port)],
      [d(`location.area.${kind}`), textOr(location.area)],
      [
        d('location.activity'),
        typeof location.activity === 'string' ? d(`activities.${location.activity}`) : '—',
      ],
      [
        d('location.berthSide'),
        typeof location.berthSide === 'string' ? d(`berthSides.${location.berthSide}`) : '—',
      ],
    ]),
  );
  const others = othersOf(report, reports);
  const phase = phaseReference(kind, others, report.reportedAt);
  if (phase) {
    out.push(
      ...section(
        b,
        d(`reference.${kind}`),
        phase.kind === 'arrival'
          ? [
              [d('reference.allFast'), b.time(phase.allFast)],
              [d('reference.firstLine'), b.time(phase.firstLine)],
              [d('reference.pilotOff'), b.time(phase.pilotOff)],
              [d('reference.tugs'), phase.tugs.length > 0 ? phase.tugs.join(' / ') : '—'],
            ]
          : [
              [d(`reference.started.${phase.kind}`), b.time(phase.reportedAt)],
              [
                d('reference.position'),
                phase.lat !== null && phase.lon !== null
                  ? `${formatLatitude(phase.lat)} ${formatLongitude(phase.lon)}`
                  : '—',
              ],
              [
                d('reference.reason'),
                phase.reason ? t(`voyage.event.options.reasons.${phase.reason}`) : '—',
              ],
            ],
      ),
    );
  }
  const previous = previousFlowReport(others, report.reportedAt);
  if (previous) {
    out.push(
      ...section(b, d('sinceLast.elapsed'), [
        [d('sinceLast.type'), t(`voyage.kinds.${previous.kind}`)],
        [d('sinceLast.time'), ltUtcText(previous.reportedAt, previous.tzOffsetMin)],
        [d('sinceLast.elapsed'), formatHours(previous.hours)],
      ]),
    );
  }
  out.push(
    ...section(b, d('etd.title'), [
      [d('etd.etd'), b.time(data.etd)],
      [d('etd.remarks'), textOr(data.etdRemarks)],
    ]),
  );
  const cargo = record(data.cargoProgress);
  const cargoUnit = typeof cargo.unit === 'string' ? cargo.unit : 'MT';
  out.push(
    ...section(b, d('cargo.title'), [
      [
        d('cargo.loadCondition'),
        typeof cargo.loadCondition === 'string'
          ? t(`voyage.event.options.loadConditions.${cargo.loadCondition}`)
          : '—',
      ],
      [
        d('cargo.operation'),
        typeof cargo.operation === 'string' ? d(`cargo.operations.${cargo.operation}`) : '—',
      ],
      [d('cargo.cargoType'), textOr(cargo.cargoType)],
      [d('cargo.planned'), unit(cargo.plannedQty, cargoUnit)],
      [d('cargo.sinceLast'), unit(cargo.handledSinceLastQty, cargoUnit)],
      [d('cargo.total'), unit(cargo.handledTotalQty, cargoUnit)],
      [d('cargo.remaining'), unit(cargoRemaining(cargo), cargoUnit)],
      [d('cargo.remarks'), textOr(cargo.remarks)],
    ]),
  );
  const hours = rows(data.runningHours).map((row) => [
    textOr(row.equipment),
    num(row.sinceLastH),
    num(row.todayH),
    num(row.totalH),
    textOr(row.remarks),
  ]);
  if (hours.length > 0) out.push(...hoursTable(t, b, hours, true));
  return out;
}
